import { type FormEvent, useCallback, useState } from "react";
import { Editor, Element, Range, Transforms } from "slate";

import {
  Button,
  getLinkAtSelection,
  TextFieldInput,
  Tooltip,
} from "@smart/itops-sb-design-system-dom";
import { EditorElement, serialize } from "@smart/itops-serialisation-basic";
import { useSmokeballApp } from "@smart/itops-smokeball-app-dom";

type LinkPopupProps = {
  editor: Editor;
  onClose: () => void;
  onChanged?: (value: string) => void;
  initialUrl?: string;
  initialDisplayText: string;
  isEditingExistingLink: boolean;
  selection: Range | null;
};

export const LinkPopup = ({
  editor,
  onClose,
  onChanged,
  initialUrl,
  initialDisplayText,
  isEditingExistingLink,
  selection,
}: LinkPopupProps) => {
  const [url, setUrl] = useState(() => initialUrl ?? "");
  const [displayText, setDisplayText] = useState(() => initialDisplayText);
  const { host, hostType } = useSmokeballApp();

  const normalizedUrl = (value: string) =>
    value.match(/^https?:\/\//) ? value : `https://${value}`;

  const canSubmit = !!displayText.trim() || !!url.trim();

  const handleSubmit = useCallback(
    (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      const trimmedUrl = url.trim();
      const text = displayText.trim() || trimmedUrl;
      if (!text) return;

      if (!isEditingExistingLink && !trimmedUrl) return;

      if (selection) {
        Transforms.select(editor, selection);
      } else {
        Transforms.select(editor, Editor.end(editor, []));
      }

      const editExistingLink = () => {
        const replaceLinkText = (textToReplace: string) => {
          const linkEntry = getLinkAtSelection(editor, editor.selection);
          if (!linkEntry) return;
          const [, path] = linkEntry;
          const contentRange: Range = {
            anchor: Editor.start(editor, path),
            focus: Editor.end(editor, path),
          };
          Transforms.select(editor, contentRange);
          Transforms.delete(editor);
          Transforms.insertText(editor, textToReplace);
        };

        const linkEntry = getLinkAtSelection(editor, editor.selection);
        if (!linkEntry) return;

        const [, path] = linkEntry;

        if (!trimmedUrl) {
          replaceLinkText(text);
          const linkAfterTextUpdate = getLinkAtSelection(
            editor,
            editor.selection,
          );
          if (linkAfterTextUpdate) {
            Transforms.unwrapNodes(editor, { at: linkAfterTextUpdate[1] });
          }
        } else {
          const newUrl = normalizedUrl(trimmedUrl);
          Transforms.setNodes(
            editor,
            { url: newUrl },
            {
              at: path,
              match: (n) => Element.isElement(n) && n.type === "link",
            },
          );
          // setNodes can normalize the paragraph (e.g. insert empty text around the link),
          // so the link path from before setNodes is no longer valid.
          replaceLinkText(text);
        }
      };

      if (isEditingExistingLink) {
        editExistingLink();
      } else {
        if (editor.selection && Range.isExpanded(editor.selection)) {
          Transforms.delete(editor);
        }
        const updated: EditorElement<"link"> = {
          type: "link",
          url: normalizedUrl(trimmedUrl),
          children: [{ text }],
        };
        Transforms.insertNodes(editor, updated);
      }

      const serialized = serialize(editor.children);
      onChanged?.(serialized);
      onClose();
    },
    [
      editor,
      selection,
      isEditingExistingLink,
      onChanged,
      onClose,
      url,
      displayText,
    ],
  );

  return (
    <form onSubmit={handleSubmit}>
      <div className="flex flex-col gap-[2.4rem] p-[2.4rem] w-[40rem]">
        <Tooltip
          text={displayText ? "Text to display" : undefined}
          placement="top"
          offsetBy={8}
        >
          <TextFieldInput
            label="Display text"
            value={displayText}
            placeholder={url.trim() || undefined}
            onChange={(e) => setDisplayText(e.currentTarget.value)}
            autoFocus
          />
        </Tooltip>
        <div className="flex gap-small">
          <Tooltip
            text={url ? "Type or paste URL" : undefined}
            placement="bottom"
            offsetBy={8}
            className="flex-1"
          >
            <TextFieldInput
              label="URL"
              placeholder="https://"
              value={url}
              onChange={(e) => setUrl(e.currentTarget.value)}
            />
          </Tooltip>
          <Button
            variant="secondarySubtle"
            leftIcon="regularArrowUpRightFromSquare"
            onClick={() => {
              const trimmed = url.trim();
              if (!trimmed) return;
              const fullUrl = normalizedUrl(trimmed);
              if (hostType === "web") {
                window.open(fullUrl, "_blank", "noopener,noreferrer");
              } else {
                host?.open({
                  url: fullUrl,
                  title: displayText.trim() || trimmed,
                });
              }
            }}
            className="self-end"
            aria-label="Open link"
            disabled={!url.trim()}
          />
        </div>
        <div className="flex flex-row justify-end gap-small">
          <Button
            variant="secondarySubtle"
            type="button"
            text="Cancel"
            onClick={() => onClose()}
          />
          <Button
            variant="primaryDefault"
            text="Save"
            type="submit"
            disabled={!canSubmit}
          />
        </div>
      </div>
    </form>
  );
};
